import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Eye, EyeOff, Briefcase, Search } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { AuthLogoImage } from '@/components/auth/auth-logo-image'
import { authApi } from '@/services/auth'
import { buildSignupEmailVerificationPath, isSignupEmailOtpVerificationContext } from '@/lib/auth-verification'
import { orgTreeApi } from '@/services/org-tree'
import { buildPostSelectionPath } from '@/lib/auth-redirect'
import { useSelectedBackendServerUrl } from '@/lib/backend-servers'
import { getErrorMessage } from '@/lib/api'
import { AuthTermsDisclosure } from '@/components/auth/auth-terms-disclosure'
import { BackendServerSelector } from '@/components/auth/backend-server-selector'
import { useRefetchOnReconnect } from '@/hooks/use-refetch-on-reconnect'
import { resolveServiceInstanceAuthLogos } from '@/lib/auth-branding'

const PHONE_NUMBER_SAMPLE_PLACEHOLDER = '9876543210'

const SIGNUP_ENV_ROOT_ORG_ID = String(import.meta.env.VITE_ROOT_ORGANIZATION_ID ?? '').trim()
const BUILD_MODE = String(import.meta.env.MODE ?? '').trim().toLowerCase()
const ENFORCE_ENV_ROOT_ORG_ID = BUILD_MODE === 'production' || BUILD_MODE === 'staging'

const companySignupSchema = z
  .object({
    parent_org_reference: z.string().trim().min(1, 'Parent org identifier or service instance id is required'),
    company_name: z.string().trim().min(1, 'Organization name is required'),
    identifier: z.string().trim().min(1, 'Identifier is required'),
    owner_name: z.string().trim().min(1, 'Owner name is required'),
    email: z.string().email('Please enter a valid email address'),
    phone_number: z.string().trim().min(1, 'Phone number is required'),
    country_id: z.string().trim().min(1, 'Country is required'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain uppercase, lowercase, and number'),
    password_confirm: z.string(),
  })
  .refine((data) => data.password === data.password_confirm, {
    message: 'Passwords do not match',
    path: ['password_confirm'],
  })

const parentOrgLookupSchema = z.object({
  reference: z.string().trim().min(1, 'Parent org identifier or service instance id is required'),
})

type CompanySignupFormData = z.infer<typeof companySignupSchema>
type ParentOrgLookupFormData = z.infer<typeof parentOrgLookupSchema>

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

export default function SignupCompanyPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const selectedBackendServerUrl = useSelectedBackendServerUrl()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isResolvingParentOrg, setIsResolvingParentOrg] = useState(false)
  const envRootOrgReference = ENFORCE_ENV_ROOT_ORG_ID ? SIGNUP_ENV_ROOT_ORG_ID : ''
  const hasConfiguredEnvRootOrgReference = envRootOrgReference.length > 0
  const requestedParentOrgReference =
    searchParams.get('org_instance_id') ||
    searchParams.get('parent_org_instance_id') ||
    searchParams.get('org_identifier') ||
    searchParams.get('parent_org_identifier') ||
    searchParams.get('tenant') ||
    ''
  const defaultParentOrgReference = ENFORCE_ENV_ROOT_ORG_ID
    ? envRootOrgReference
    : requestedParentOrgReference

  const {
    register: registerLookup,
    handleSubmit: handleLookupSubmit,
    setValue: setLookupValue,
    formState: { errors: lookupErrors },
  } = useForm<ParentOrgLookupFormData>({
    resolver: zodResolver(parentOrgLookupSchema),
    defaultValues: {
      reference: defaultParentOrgReference,
    },
  })

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CompanySignupFormData>({
    resolver: zodResolver(companySignupSchema),
    defaultValues: {
      country_id: '94',
      parent_org_reference: defaultParentOrgReference,
    },
  })

  const companyName = watch('company_name')
  const selectedCountryId = watch('country_id')

  const parentOrgQuery = useQuery({
    queryKey: ['company-signup-parent-org', selectedBackendServerUrl, defaultParentOrgReference],
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(defaultParentOrgReference),
    enabled: defaultParentOrgReference.length >= 3,
    staleTime: 60_000,
    retry: false,
    placeholderData: (previousData) => previousData,
  })

  const parentOrganization = parentOrgQuery.data
  const parentOrganizationName = parentOrganization?.name
    || parentOrganization?.title
    || parentOrganization?.identifier
    || parentOrganization?.url_identifier
    || defaultParentOrgReference
    || 'Parent organization'
  const { sidePanelLogos: parentSidePanelLogos } = resolveServiceInstanceAuthLogos(parentOrganization)
  const termsText = parentOrganization?.terms_and_conditions ?? null
  const privacyText = parentOrganization?.privacy_policy ?? null
  const hasParentOrganizationLoadError = Boolean(defaultParentOrgReference)
    && parentOrgQuery.isError
  const hasInvalidParentOrgParam = Boolean(defaultParentOrgReference)
    && !parentOrgQuery.isLoading
    && !parentOrgQuery.isError
    && !parentOrganization
  const hasMissingEnvRootOrgConfiguration = ENFORCE_ENV_ROOT_ORG_ID && !hasConfiguredEnvRootOrgReference
  const showParentOrgLookupStep = hasMissingEnvRootOrgConfiguration
    || (!ENFORCE_ENV_ROOT_ORG_ID && !defaultParentOrgReference)

  useRefetchOnReconnect({
    enabled: hasParentOrganizationLoadError,
    refetch: parentOrgQuery.refetch,
  })

  const countriesQuery = useQuery({
    queryKey: ['org-tree-countries', selectedBackendServerUrl],
    queryFn: orgTreeApi.listCountries,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (!companyName) {
      return
    }

    setValue('identifier', toSlug(companyName), { shouldValidate: false, shouldDirty: false })
  }, [companyName, setValue])

  useEffect(() => {
    if (selectedCountryId || !countriesQuery.data?.length) {
      return
    }

    setValue('country_id', String(countriesQuery.data[0].id), { shouldValidate: false, shouldDirty: false })
  }, [countriesQuery.data, selectedCountryId, setValue])

  useEffect(() => {
    if (!defaultParentOrgReference) {
      return
    }

    setValue('parent_org_reference', defaultParentOrgReference, { shouldValidate: false, shouldDirty: false })
    setLookupValue('reference', defaultParentOrgReference, { shouldValidate: false, shouldDirty: false })
  }, [defaultParentOrgReference, setLookupValue, setValue])

  const onLookupSubmit = async ({ reference }: ParentOrgLookupFormData) => {
    const normalizedReference = reference.trim()
    setIsResolvingParentOrg(true)
    try {
      const instance = await orgTreeApi.resolvePublicSignupServiceInstance(normalizedReference)
      const resolvedReference = instance?.instance_id || String(instance?.id ?? normalizedReference)
      navigate(`/signup/company?org_instance_id=${encodeURIComponent(resolvedReference)}`, { replace: true })
    } catch {
      navigate(`/signup/company?org_instance_id=${encodeURIComponent(normalizedReference)}`, { replace: true })
    } finally {
      setIsResolvingParentOrg(false)
    }
  }

  const clearParentOrganizationSelection = () => {
    if (ENFORCE_ENV_ROOT_ORG_ID) {
      return
    }
    navigate('/signup/company', { replace: true })
  }
  const onSubmit = async (data: CompanySignupFormData) => {
    setIsLoading(true)
    try {
      const response = await authApi.signup({
        signup_type: 'company',
        company_name: data.company_name,
        identifier: data.identifier,
        owner_name: data.owner_name,
        email: data.email,
        org_instance_id: data.parent_org_reference.trim(),
        country_id: Number(data.country_id),
        phone_number: data.phone_number,
        password: data.password,
      })
      const loginTenant = response.data.company_identifier || response.data.service_instance_id

      if (isSignupEmailOtpVerificationContext(response.data.context) && data.email.trim()) {
        navigate(buildSignupEmailVerificationPath({
          email: data.email.trim(),
          tenant: loginTenant,
          orgInstanceId: response.data.service_instance_id || loginTenant,
          otpLength: response.data.otp_length,
        }), { replace: true })
        return
      }

      if (loginTenant) {
        try {
          const loginResponse = await authApi.login({
            tenant: loginTenant,
            email: data.email,
            password: data.password,
          })

          if (loginResponse.data?.access_token) {
            toast.success('Account created successfully! Select a post to continue.')
            navigate(buildPostSelectionPath(loginResponse.data.service_instance_id || loginTenant), { replace: true })
            return
          }
        } catch {
          // Signup succeeded; fall through to the sign-in redirect.
        }
      }

      toast.success('Account created successfully! Please sign in.')
      const loginParams = new URLSearchParams()
      if (loginTenant) {
        loginParams.set('tenant', loginTenant)
      }
      navigate(loginParams.toString() ? `/login?${loginParams.toString()}` : '/login')
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-primary/5 px-4 py-10">
      <div aria-hidden className="pointer-events-none absolute -left-32 top-10 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -right-28 bottom-8 h-[28rem] w-[28rem] rounded-full bg-primary/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 h-[40rem] w-[40rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl" />

      <div className="relative mx-auto grid w-full max-w-6xl items-stretch overflow-hidden rounded-[36px] border border-white/70 bg-white/95 shadow-[0_28px_90px_rgba(15,23,42,0.18)] lg:grid-cols-[0.95fr_1.05fr]">
        <section className="relative isolate hidden items-center justify-center overflow-hidden bg-white p-8 lg:flex">
          <div aria-hidden className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          <div aria-hidden className="absolute -bottom-28 right-8 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />
          <AuthLogoImage
            sources={parentSidePanelLogos}
            alt={`${parentOrganizationName} logo`}
            className="relative z-10 max-h-44 w-auto object-contain drop-shadow-xl sm:max-h-52"
            fallback={(
              <div className="relative z-10 flex h-36 w-36 items-center justify-center rounded-[34px] bg-primary/10 text-primary shadow-sm sm:h-44 sm:w-44">
                <Briefcase className="h-16 w-16" />
              </div>
            )}
          />
        </section>

        <div className="relative isolate flex h-full w-full flex-col overflow-hidden bg-primary p-6 text-primary-foreground sm:p-8">
        <div aria-hidden className="absolute -right-28 -top-24 h-96 w-96 rounded-full bg-white/20" />
        <div aria-hidden className="absolute -bottom-44 right-8 h-[30rem] w-[30rem] rounded-full bg-slate-900/10" />
        <div aria-hidden className="absolute -right-28 top-1/2 h-[34rem] w-[34rem] -translate-y-1/2 rounded-full border border-white/15" />
        <div className="relative z-10 mb-8 flex flex-col items-center text-center">
          <h1 className="text-2xl font-bold text-white">Create an organization</h1>
          <p className="mt-1 text-sm text-white/80">Register a new organization account for the portal.</p>
        </div>

        <Card className="relative z-10 rounded-[28px] border-0 bg-white/95 text-foreground shadow-2xl shadow-slate-950/20 backdrop-blur">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl">{showParentOrgLookupStep ? 'Find the parent organization' : 'Organization signup'}</CardTitle>
            <CardDescription>
              {showParentOrgLookupStep
                ? 'Choose the parent organization before entering the new organization details.'
                : 'Set the organization name and identifier, then create the initial admin account.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!ENFORCE_ENV_ROOT_ORG_ID ? (
              <BackendServerSelector disabled={isLoading || isResolvingParentOrg} className="mb-4" />
            ) : null}
            {hasParentOrganizationLoadError ? (
              <div className="mb-4 rounded-lg border border-amber-300/50 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <p>Unable to verify parent organization details right now. You can still continue with the selected reference.</p>
                <button
                  type="button"
                  onClick={() => {
                    void parentOrgQuery.refetch()
                  }}
                  className="mt-2 font-medium text-primary hover:underline"
                >
                  Retry
                </button>
              </div>
            ) : null}
            {hasInvalidParentOrgParam && !hasMissingEnvRootOrgConfiguration ? (
              <div className="mb-4 rounded-lg border border-amber-300/50 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Parent organization details could not be verified. Signup will use the entered reference during submission.
              </div>
            ) : null}
            {showParentOrgLookupStep ? (
              <form onSubmit={handleLookupSubmit(onLookupSubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reference">Parent organization reference</Label>
                  <Input
                    id="reference"
                    placeholder="Parent org identifier or service instance id"
                    autoComplete="off"
                    spellCheck={false}
                    disabled={isResolvingParentOrg}
                    {...registerLookup('reference')}
                    aria-invalid={!!lookupErrors.reference}
                  />
                  <p className="text-xs text-muted-foreground">Enter the parent organization identifier or its service instance id.</p>
                  {lookupErrors.reference ? <p className="text-sm text-destructive">{lookupErrors.reference.message}</p> : null}
                </div>

                <Button type="submit" className="w-full" disabled={isResolvingParentOrg}>
                  {isResolvingParentOrg ? <Spinner className="mr-2" /> : <Search className="mr-2 h-4 w-4" />}
                  Continue to organization form
                </Button>
              </form>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <input type="hidden" {...register('parent_org_reference')} />
                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                  Creating this organization under <span className="font-medium text-foreground">{parentOrganizationName}</span>.
                  {!ENFORCE_ENV_ROOT_ORG_ID ? (
                    <button type="button" onClick={clearParentOrganizationSelection} className="ml-2 font-medium text-primary hover:underline">
                      Change parent organization
                    </button>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_name">Organization name</Label>
                  <Input id="company_name" placeholder="Acme Services" disabled={isLoading} {...register('company_name')} aria-invalid={!!errors.company_name} />
                  {errors.company_name ? <p className="text-sm text-destructive">{errors.company_name.message}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="identifier">Identifier</Label>
                  <Input id="identifier" placeholder="acme-services" disabled={isLoading} {...register('identifier')} aria-invalid={!!errors.identifier} />
                  {errors.identifier ? <p className="text-sm text-destructive">{errors.identifier.message}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="owner_name">Owner name</Label>
                  <Input id="owner_name" placeholder="Jane Doe" disabled={isLoading} {...register('owner_name')} aria-invalid={!!errors.owner_name} />
                  {errors.owner_name ? <p className="text-sm text-destructive">{errors.owner_name.message}</p> : null}
                </div>

                <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country_id">Country</Label>
                  <input type="hidden" {...register('country_id')} />
                  <Select value={selectedCountryId || ''} onValueChange={(value) => setValue('country_id', value, { shouldValidate: true, shouldDirty: true })} disabled={isLoading || countriesQuery.isLoading || !countriesQuery.data?.length}>
                    <SelectTrigger id="country_id" aria-invalid={!!errors.country_id} className="w-full">
                      <SelectValue placeholder={countriesQuery.isLoading ? 'Loading countries...' : 'Select a country'} />
                    </SelectTrigger>
                    <SelectContent>
                      {(countriesQuery.data ?? []).map((country) => (
                        <SelectItem key={String(country.id)} value={String(country.id)}>
                          {[country.country_name, country.dial_code].filter(Boolean).join(' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {countriesQuery.isError && !errors.country_id ? <p className="text-sm text-destructive">Unable to load countries right now.</p> : null}
                  {errors.country_id ? <p className="text-sm text-destructive">{errors.country_id.message}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone_number">Phone number</Label>
                  <Input id="phone_number" type="tel" placeholder={PHONE_NUMBER_SAMPLE_PLACEHOLDER} autoComplete="tel" disabled={isLoading} {...register('phone_number')} aria-invalid={!!errors.phone_number} />
                  {errors.phone_number ? <p className="text-sm text-destructive">{errors.phone_number.message}</p> : null}
                </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" placeholder="name@example.com" autoComplete="email" disabled={isLoading} {...register('email')} aria-invalid={!!errors.email} />
                  {errors.email ? <p className="text-sm text-destructive">{errors.email.message}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Create a password" autoComplete="new-password" disabled={isLoading} {...register('password')} aria-invalid={!!errors.password} />
                    <button type="button" onClick={() => setShowPassword((current) => !current)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password ? <p className="text-sm text-destructive">{errors.password.message}</p> : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password_confirm">Confirm password</Label>
                  <div className="relative">
                    <Input id="password_confirm" type={showConfirmPassword ? 'text' : 'password'} placeholder="Confirm your password" autoComplete="new-password" disabled={isLoading} {...register('password_confirm')} aria-invalid={!!errors.password_confirm} />
                    <button type="button" onClick={() => setShowConfirmPassword((current) => !current)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password_confirm ? <p className="text-sm text-destructive">{errors.password_confirm.message}</p> : null}
                </div>

                <AuthTermsDisclosure termsText={termsText} privacyText={privacyText} />

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? <Spinner className="mr-2" /> : null}
                  Create organization account
                </Button>
              </form>
            )}

            <div className="mt-6 rounded-xl border border-border/70 bg-muted/30 p-4">
              <p className="mb-2 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">Other access options</p>
              <div className="space-y-2 text-center text-sm">
              <div>
                <span className="text-muted-foreground">Joining an existing organization? </span>
                <Link to="/signup" className="font-medium text-primary hover:underline">
                  Signup
                </Link>
              </div>
              <div>
                <span className="text-muted-foreground">Already have an account? </span>
                <Link to="/login" className="font-medium text-primary hover:underline">
                  Sign in
                </Link>
              </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  )
}